import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight, Sparkles, Star } from "lucide-react";
import {
  getDropsNav,
  getDropDetail,
  DropNavDto,
  DropDetailDto,
} from "../api/dropApi";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

type DropPageState = {
  collection: DropDetailDto | null;
  products: any[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

function safeMsg(e: any) {
  return e?.response?.data?.message || e?.message || "Request failed";
}

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function getProductImage(product: any, seed: string) {
  return (
    product?.thumbnailUrl ||
    product?.thumbnail ||
    product?.imageUrl ||
    product?.imageUrls?.[0] ||
    `https://source.unsplash.com/random/1200x1500?fashion,editorial&sig=${seed}`
  );
}

function getHeroImage(meta: any, fallbackProduct?: any) {
  return (
    meta?.heroImageUrl ||
    meta?.featuredImageUrl ||
    getProductImage(fallbackProduct, "drop-hero")
  );
}

function DropChip(props: {
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        "inline-flex h-10 items-center rounded-full border px-4 text-[10px] font-medium uppercase tracking-[0.16em] transition",
        props.active
          ? "border-brand-brown bg-brand-brown text-brand-ivory shadow-[0_16px_36px_rgba(43,31,26,0.14)]"
          : "border-brand-brown/10 bg-white/78 text-brand-brown/72 hover:border-brand-brown/22 hover:bg-white hover:text-brand-brown",
      )}
    >
      {props.children}
    </button>
  );
}

function ProductCard(props: {
  product: any;
  season: string;
  to: string;
  featured?: boolean;
  viewText: string;
  featuredText: string;
}) {
  const { product, season, to, featured, viewText, featuredText } = props;
  const image = getProductImage(
    product,
    `${product?.productId ?? product?.id ?? product?.name ?? "drop"}`,
  );

  return (
    <Link
      to={to}
      className={cn("group block", featured && "lg:col-span-2")}
    >
      <article className="overflow-hidden rounded-[28px] border border-brand-brown/10 bg-white/76 shadow-[0_24px_80px_rgba(43,31,26,0.08)] transition duration-500 hover:-translate-y-1 hover:shadow-[0_34px_110px_rgba(43,31,26,0.12)]">
        <div className={cn("overflow-hidden bg-[#f2eadf]", featured ? "aspect-[16/10]" : "aspect-[4/5]")}> 
          <img
            src={image}
            alt={product?.name || "Drop piece"}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            loading="lazy"
          />
        </div>

        <div className="space-y-4 p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex h-8 items-center rounded-full border border-brand-brown/10 bg-brand-brown/5 px-3 text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/64">
              {season || "Drop"}
            </span>
            {featured && (
              <span className="inline-flex h-8 items-center rounded-full border border-brand-brown/10 bg-white px-3 text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/72">
                {featuredText}
              </span>
            )}
          </div>

          <h3
            className={cn(
              "font-medium leading-[1.02] tracking-[0.01em] text-brand-brown",
              featured ? "text-[26px] sm:text-[34px]" : "text-[18px] sm:text-[20px]",
            )}
          >
            {product?.name || "Selected piece"}
          </h3>

          <div className="flex items-center justify-between border-t border-brand-brown/8 pt-4 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/68 transition group-hover:text-brand-brown">
            <span>{viewText}</span>
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
          </div>
        </div>
      </article>
    </Link>
  );
}

export default function Drops() {
  const { i18n } = useTranslation();
  const q = useQuery();
  const navigate = useNavigate();

  const isVi = !i18n.language?.startsWith("en");
  const copy = isVi
    ? {
        kicker: "OWL DREAM / DROP",
        heroTitle: "Identity in pieces.",
        heroDesc: "Drop được chọn để nhìn ra chất ngay, không cần quá nhiều chữ.",
        latest: "Drop hiện tại",
        featured: "Nổi bật",
        viewPiece: "Xem sản phẩm",
        explore: "Xem collection",
        allDrops: "Các drop",
        selected: "Sản phẩm chọn lọc",
        productFocus: "Sản phẩm là trọng tâm.",
        productSub: "Ít chữ hơn. Ảnh và form lên trước.",
        emptyTitle: "Drop này chưa có sản phẩm.",
        emptyDesc: "Hãy xem collection để khám phá thêm các thiết kế khác.",
        shopCollection: "Xem collection",
        backHome: "Về trang chủ",
        ctaTitle: "Quiet. Sharp. Distinct.",
        ctaDesc: "Streetwear thiên về bản sắc, không chạy theo ồn ào.",
        loading: "Đang tải...",
        noDrop: "Chưa có drop",
      }
    : {
        kicker: "OWL DREAM / DROP",
        heroTitle: "Identity in pieces.",
        heroDesc: "A cleaner drop page with the product doing the talking.",
        latest: "Current drop",
        featured: "Featured",
        viewPiece: "View piece",
        explore: "Shop collection",
        allDrops: "Drops",
        selected: "Selected pieces",
        productFocus: "Product comes first.",
        productSub: "Less copy. More image. Clearer focus.",
        emptyTitle: "This drop has no products yet.",
        emptyDesc: "Explore the collection to see more pieces.",
        shopCollection: "Shop collection",
        backHome: "Back home",
        ctaTitle: "Quiet. Sharp. Distinct.",
        ctaDesc: "Streetwear built around identity, not noise.",
        loading: "Loading...",
        noDrop: "No drop yet",
      };

  const era = q.get("era") || "new";
  const activeSlug = q.get("drop") || "";

  const [tabs, setTabs] = useState<DropNavDto[]>([]);
  const [loadingTabs, setLoadingTabs] = useState(true);
  const [data, setData] = useState<DropPageState | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [err, setErr] = useState<string>("");

  useEffect(() => {
    let alive = true;
    setLoadingTabs(true);
    setErr("");

    getDropsNav({ era, isActive: true })
      .then((rows) => {
        if (!alive) return;

        const sorted = [...(rows || [])].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
        );
        setTabs(sorted);

        if (!activeSlug && sorted.length > 0) {
          navigate(
            `/drops?era=${encodeURIComponent(era)}&drop=${encodeURIComponent(sorted[0].slug)}`,
            { replace: true },
          );
          return;
        }

        if (activeSlug && sorted.length > 0) {
          const ok = sorted.some((x) => x.slug === activeSlug);
          if (!ok) {
            navigate(
              `/drops?era=${encodeURIComponent(era)}&drop=${encodeURIComponent(sorted[0].slug)}`,
              { replace: true },
            );
          }
        }
      })
      .catch((e: any) => {
        if (!alive) return;
        setErr(safeMsg(e) || "Failed to load drops");
      })
      .finally(() => alive && setLoadingTabs(false));

    return () => {
      alive = false;
    };
  }, [era, activeSlug, navigate]);

  useEffect(() => {
    let alive = true;

    if (!activeSlug) {
      setData(null);
      setLoadingDetail(false);
      return () => {
        alive = false;
      };
    }

    setLoadingDetail(true);
    setErr("");

    getDropDetail(activeSlug, { page: 1, pageSize: 9, sort: "order" })
      .then((res) => {
        if (!alive) return;
        setData(res as any);
      })
      .catch((e: any) => {
        if (!alive) return;
        setErr(safeMsg(e) || "Failed to load drop detail");
        setData(null);
      })
      .finally(() => alive && setLoadingDetail(false));

    return () => {
      alive = false;
    };
  }, [activeSlug]);

  const go = (slug: string) => {
    navigate(`/drops?era=${encodeURIComponent(era)}&drop=${encodeURIComponent(slug)}`);
  };

  const meta = data?.collection;
  const products = data?.products || [];
  const heroImage = getHeroImage(meta, products[0]);
  const label = meta?.name || copy.noDrop;
  const season = meta?.season || era;
  const shortText = meta?.shortDesc || meta?.description || copy.heroDesc;
  const hasTabs = tabs.length > 0;

  return (
    <div className="min-h-screen overflow-x-hidden bg-brand-ivory text-brand-brown">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[#f7f0e5]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(205,170,111,0.12),transparent_22%),radial-gradient(circle_at_bottom_right,rgba(43,31,26,0.05),transparent_28%)]" />
      </div>

      {err ? (
        <div className="mx-auto max-w-7xl px-4 pt-24 sm:px-6 lg:pt-28">
          <div className="rounded-[24px] border border-red-500/20 bg-red-500/5 p-5 text-sm text-red-700">
            {err}
          </div>
        </div>
      ) : null}

      <section className="pt-24 sm:pt-28 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-6 lg:grid-cols-[0.82fr_1.18fr] lg:items-stretch lg:gap-8">
            <div className="flex flex-col justify-between rounded-[30px] border border-brand-brown/10 bg-white/74 p-6 shadow-[0_24px_80px_rgba(43,31,26,0.08)] sm:p-8 lg:p-10">
              <div>
                <div className="inline-flex items-center gap-3 rounded-full border border-brand-brown/10 bg-brand-ivory px-4 py-2">
                  <Sparkles className="h-4 w-4 text-brand-gold" />
                  <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/66">
                    {copy.kicker}
                  </span>
                </div>

                <p className="mt-8 text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/48">
                  {copy.latest}
                </p>

                <h1 className="mt-4 text-[38px] font-medium leading-[0.94] tracking-[0.01em] text-brand-brown sm:text-[52px] lg:text-[64px]">
                  {loadingDetail ? copy.loading : label}
                </h1>

                <p className="mt-5 max-w-md text-sm leading-7 text-brand-brown/62 sm:text-[15px] sm:leading-8 line-clamp-3">
                  {loadingDetail ? "" : shortText}
                </p>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <span className="inline-flex h-10 items-center rounded-full border border-brand-brown/10 bg-brand-brown/5 px-4 text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/68">
                  <Star className="mr-2 h-4 w-4 text-brand-gold" />
                  {season}
                </span>
                <Link
                  to="/collection"
                  className="inline-flex h-11 items-center gap-2 rounded-full bg-brand-brown px-5 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-ivory transition hover:opacity-90"
                >
                  {copy.explore}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="overflow-hidden rounded-[30px] border border-brand-brown/10 bg-white/62 shadow-[0_28px_100px_rgba(43,31,26,0.1)]">
              <div className="aspect-[16/12] overflow-hidden bg-[#efe7dc] sm:aspect-[16/11] lg:aspect-[16/10]">
                <img
                  src={heroImage}
                  alt={label}
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="sticky top-[74px] z-30 mt-6 border-y border-brand-brown/10 bg-brand-ivory/88 backdrop-blur-md sm:top-[80px]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/50">
              {copy.allDrops}
            </div>

            <div className="flex flex-1 flex-wrap justify-end gap-2">
              {loadingTabs ? (
                <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/55">
                  {copy.loading}
                </div>
              ) : !hasTabs ? (
                <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/55">
                  {copy.noDrop}
                </div>
              ) : (
                tabs.map((d) => (
                  <DropChip
                    key={d.slug}
                    active={d.slug === activeSlug}
                    onClick={() => go(d.slug)}
                  >
                    {d.name}
                  </DropChip>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <section id="drop-grid" className="py-10 sm:py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/48">
                {copy.selected}
              </p>
              <h2 className="mt-3 text-[28px] font-medium leading-[0.98] tracking-[0.01em] text-brand-brown sm:text-[38px] lg:text-[44px]">
                {copy.productFocus}
              </h2>
              <p className="mt-3 text-sm leading-7 text-brand-brown/60">
                {copy.productSub}
              </p>
            </div>

            <Link
              to="/collection"
              className="inline-flex items-center gap-2 self-start text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/70 transition hover:text-brand-brown"
            >
              {copy.shopCollection}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          {products.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
              {products.map((p: any, i: number) => {
                const to = `/product/${p?.productId ?? p?.id ?? ""}`;
                return (
                  <ProductCard
                    key={p?.productId ?? p?.id ?? i}
                    product={p}
                    season={season}
                    to={to}
                    featured={i === 0}
                    viewText={copy.viewPiece}
                    featuredText={copy.featured}
                  />
                );
              })}
            </div>
          ) : !loadingDetail && hasTabs ? (
            <div className="rounded-[30px] border border-brand-brown/10 bg-white/72 p-8 text-center shadow-[0_24px_80px_rgba(43,31,26,0.08)] sm:p-12">
              <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/46">
                {copy.latest}
              </p>
              <h3 className="mt-4 text-[28px] font-medium leading-[1] text-brand-brown sm:text-[36px]">
                {copy.emptyTitle}
              </h3>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-brand-brown/62">
                {copy.emptyDesc}
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/collection"
                  className="inline-flex h-12 items-center gap-3 rounded-full bg-brand-brown px-6 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-ivory transition hover:opacity-90"
                >
                  {copy.shopCollection}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/"
                  className="inline-flex h-12 items-center gap-3 rounded-full border border-brand-brown/12 px-6 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/74 transition hover:border-brand-brown/28 hover:bg-brand-brown/5"
                >
                  {copy.backHome}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "overflow-hidden rounded-[30px] border border-brand-brown/10 bg-white/72 shadow-[0_24px_80px_rgba(43,31,26,0.08)]",
                    i === 0 && "sm:col-span-2 lg:col-span-2",
                  )}
                >
                  <div
                    className={cn(
                      "animate-pulse bg-brand-brown/6",
                      i === 0 ? "aspect-[16/10]" : "aspect-[4/5]",
                    )}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="border-t border-brand-brown/10 pb-16 pt-12 sm:pb-20 sm:pt-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="rounded-[32px] border border-brand-brown/10 bg-[#211917] px-6 py-8 text-brand-ivory shadow-[0_36px_120px_rgba(0,0,0,0.16)] sm:px-8 sm:py-10">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-ivory/56">
                  OWL DREAM
                </p>
                <h3 className="mt-3 text-[28px] font-medium leading-[0.98] text-brand-ivory sm:text-[40px]">
                  {copy.ctaTitle}
                </h3>
                <p className="mt-4 text-sm leading-7 text-brand-ivory/68">
                  {copy.ctaDesc}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to="/collection"
                  className="inline-flex h-12 items-center gap-3 rounded-full bg-white px-6 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown transition hover:bg-brand-ivory"
                >
                  {copy.shopCollection}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>

                <Link
                  to="/"
                  className="inline-flex h-12 items-center gap-3 rounded-full border border-white/12 bg-white/8 px-6 text-[11px] font-medium uppercase tracking-[0.14em] text-white transition hover:bg-white/12"
                >
                  {copy.backHome}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
